<template>
	<div class="appointment">
		<div style="position: sticky; top:12px; z-index: 200;">
			<Breadcrumbs :items="items" />
			<v-card>
				<v-tabs v-model="tabOpen" centered grow active-class="secondary">
					<v-tab href="#clinical">
						<v-icon left>favorite</v-icon>
						<translate>Clinic H.</translate>
					</v-tab>
					<v-tab href="#anamnesis">
						<v-icon left>mdi-clipboard-alert</v-icon>
						<translate>Anamnesis</translate>
					</v-tab>
					<v-tab href="#tests">
						<v-icon left>assessment</v-icon>
						<translate>Tests</translate>
					</v-tab>
					<v-tab href="#diagnosis">
						<v-icon left>mdi-clipboard-check</v-icon>
						<translate>Diagnosis</translate>
					</v-tab>
					<v-tab href="#treatments">
						<v-icon left>mdi-clipboard-text</v-icon>
						<translate>Treatments</translate>
					</v-tab>
				</v-tabs>
			</v-card>
		</div>

		<v-tabs-items v-model="tabOpen" class="mt-2">
			<v-tab-item :transition="false" :reverse-transition="false" value="clinical">
				<ClinicHistory
					ref="clinicHistory"
					:appointments="appointments"
					:patientAppointment="appointment"
					:documents="documents"
					:patient="patient"
					:inAppointment="true"
					:key="clinicKey"
				/>
			</v-tab-item>
			<v-tab-item :transition="false" :reverse-transition="false" value="anamnesis">
				<v-row v-if="loadingAnamnesis" no-gutters justify="center" align="center" style="height: 600px;">
					<v-progress-circular
						:size="50"
						color="primary"
						indeterminate
					></v-progress-circular>
				</v-row>
				<Anamnesis
					v-else
					:appointment="appointment"
					:drugs="appointment.drugs"
					:icds="appointment.symptoms"
					:documents="documents"
					@import-anamnese="importAppointmentAnamnese"
					@import-drugs="importAppointmentDrugs"
				/>
			</v-tab-item>
			<v-tab-item :transition="false" :reverse-transition="false" value="tests">
				<v-row v-if="loadingTests" no-gutters justify="center" align="center" style="height: 600px;">
					<v-progress-circular
						:size="50"
						color="primary"
						indeterminate
					></v-progress-circular>
				</v-row>
				<Tests v-else :tests="appointment.tests" :customTests="appointment.custom_tests" :patient="patient" :appointment="appointment" />
			</v-tab-item>
			<v-tab-item :transition="false" :reverse-transition="false" value="diagnosis">
				<v-row v-if="loadingDiagnosis" no-gutters justify="center" align="center" style="height: 600px;">
					<v-progress-circular
						:size="50"
						color="primary"
						indeterminate
					></v-progress-circular>
				</v-row>
				<Diagnosis v-else :appointment="appointment" :icds="appointment.symptoms" />
			</v-tab-item>
			<v-tab-item :transition="false" :reverse-transition="false" value="treatments">
				<v-row v-if="loadingTreatments" no-gutters justify="center" align="center" style="height: 600px;">
					<v-progress-circular
						:size="50"
						color="primary"
						indeterminate
					></v-progress-circular>
				</v-row>
				<Treatments
					v-else
					:patient="patient"
					:acuPoints="appointment.acu_points"
					:supplements="appointment.supplements"
					:formulas="appointment.formulas"
					:generalTreatments="appointment.other_treatments"
					:customTreatments="appointment.custom_treatments"
					:appointment="appointment"
				/>
			</v-tab-item>
		</v-tabs-items>
		
		<v-bottom-sheet
      v-model="showPricing"
      inset
			v-if="$store.getters.pricing"
    >
      <template v-slot:activator="{ }">
				<v-fab-transition>
					<v-btn
						small
						color="primary"
						fab
						bottom
						right
						fixed
						@click="getPricing"
						:loading="loadingAppointmentPricing"
					>
						<v-icon>mdi-comment</v-icon>
					</v-btn>
				</v-fab-transition>
      </template>
      <v-sheet rounded>
				<v-card rounded v-if="showPricing">
					<v-card-text>
						<v-row no-gutters>
							<v-col cols="12" v-if="$store.getters.appointmentType(appointment.id)" class="mb-4">
								<span>{{$store.getters.appointmentTypeName(appointment.id)}}</span>
								<span class="font-weight-bold ml-4">{{$store.getters.appointmentTypePrice(appointment.id)}}€</span>
							</v-col>
							<v-col cols="4">
								<p class="primary--text" v-translate>Tests</p>
								<v-list>
									<p v-if="$store.getters.getExistingTests(appointment.id).length == 0" class="font-weight-thin ml-5" v-translate>No tests.</p>
									<v-list-item v-else v-for="test in $store.getters.getExistingTests(appointment.id)" :key="test.code + test.id" dense>
										<v-list-item-content>
											<v-list-item-title>
												{{getName(test)}}
											</v-list-item-title>
											<v-list-item-subtitle>
												<span v-if="test.price == 'included'">Incluido</span>
												<span v-else-if="test.price">{{test.price}} €</span>
												<span v-else>Preço não definido</span>
											</v-list-item-subtitle>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-col>
							<v-col cols="5">
								<p class="primary--text" v-translate>Treatments</p>
								<v-list>
									<p v-if="$store.getters.getExistingTreatments(appointment.id).length == 0" class="font-weight-thin ml-5" v-translate>No treatments.</p>
									<v-list-item v-else v-for="treatment in $store.getters.getExistingTreatments(appointment.id)" :key="treatment.code + treatment.id" dense>
										<v-list-item-content>
											<v-list-item-title>
												{{getName(treatment)}}
											</v-list-item-title>
											<v-list-item-subtitle>
												<span v-if="treatment.price == 'included'">Incluido</span>
												<span v-else-if="treatment.price">{{treatment.price}} €</span>
												<span v-else>Preço não definido</span>
											</v-list-item-subtitle>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-col>
							<v-col cols="3">
								<p class="primary--text">Preço</p>
								<v-row no-gutters align="center">
									<v-col cols="12">
										<p class="ma-0">Definido</p>
										<v-row no-gutters align="center">
											<span class="text-h2" v-if="$store.getters.appointmentPrice(appointment.id) != null">{{$store.getters.appointmentPrice(appointment.id)}}€</span>
											<span class="font-weight-thin" v-else>Não definido</span>
											<v-btn
												color="primary"
												fab
												x-small
												class="my-1 ml-2"
												@click="showEditPrice = !showEditPrice"
											>
												<v-icon>mdi-pencil</v-icon>
											</v-btn>
											<v-col cols="12">
											<v-form ref="appPrice">
												<v-row v-if="showEditPrice" no-gutters>
													<v-text-field
														v-if="showEditPrice"
														v-model="appointmentPrice"
														outlined
														hide-details
														dense
														style="max-width: 60px"
														class="mx-2"
														:rules="[required, isNumber]"
													></v-text-field>
													<span :class="getPercentage() > 0 ? 'green--text' : 'red--text'" class="mr-3 font-weight-bold" v-if="getPercentage()">{{getPercentage()}} %</span>
												</v-row>
											</v-form>
											</v-col>
										</v-row>
									</v-col>
									<v-col class="mt-4">
										<p class="ma-0">Calculado</p>
										<span class="text-h5" v-if="$store.getters.appointmentPriceResult(appointment.id) != null">{{$store.getters.appointmentPriceResult(appointment.id)}}€</span>
										<span v-else class="font-weight-thin">Sem informação</span>
									</v-col>
									<v-col cols="12" class="mt-4">
										<v-textarea
											filled
											class="text-body-2"
											:placeholder="$gettext('Notes')"
											v-model="appointmentPriceNotes"
											auto-grow
										></v-textarea>
									</v-col>
								</v-row>
							</v-col>
						</v-row>
					</v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-btn color="primary" text @click="resetEditPrice">
							<translate>Cancel</translate>
						</v-btn>
						<v-spacer></v-spacer>
						<v-btn color="primary" text @click="saveAppointmentPrice" :loading="savingAppointmentPrice">
							<translate>Save</translate>
						</v-btn>
					</v-card-actions>
				</v-card>
      </v-sheet>
    </v-bottom-sheet>
	</div>
</template>

<script>
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import ClinicHistory from "@/components/patient/ClinicHistory.vue";
import Anamnesis from "@/components/appointment/anamnesis/Anamnesis.vue";
import Tests from "@/components/appointment/tests/Tests.vue";
import Diagnosis from "@/components/appointment/diagnosis/Diagnosis.vue";
import Treatments from "@/components/appointment/treatments/Treatments.vue";
import axios from "axios";
import { mapGetters } from 'vuex'

export default {
	name: "Appointment",
	components: {
		Breadcrumbs,
		ClinicHistory,
		Anamnesis,
		Tests,
		Diagnosis,
		Treatments
	},
	props: {
		tab: {
			default: "clinical"
		},
		fromPatient: {
			default: false
		},
		historyAppointments: {
			default: () => []
		},
		historyDocuments: {
			default: () => []
		},
		historyPatient: {
			default: () => {}
		}
	},
	data: () => ({
		tabOpen: "",
		appointment: {
			drugs: [],
			symptoms: [],
			tests: [],
			acu_points: [],
			supplements: {},
			formulas: {},
			other_treatments: []
		},
		patient: {id: null},
		appointments: [],
		documents: [],
		loadingTreatments: true,
		loadingTests: true,
		loadingAnamnesis: true,
		loadingDiagnosis: true,
		clinicKey: 0,
		showPricing: false,
		appointmentPrice: null,
		appointmentPriceNotes: '',
		loadingAppointmentPricing: false,
		showEditPrice: false,
		savingAppointmentPrice: false
	}),
	methods: {
		init() {
			//call api
			console.log("CALL API");
			const patientId = this.$route.params.pid;
			const appointmentId = this.$route.params.aid;

			axios
				.get("/patient/" + patientId + "/appointment/" + appointmentId, { params: {history: !this.fromPatient} })
				.then(response => {
					console.log(response.data);
					if (response.status === 200) {
						if(this.fromPatient) {
							this.patient = this.historyPatient;
							this.appointments = this.historyAppointments;
							this.documents = this.historyDocuments;
							for(let i=0; i < this.appointments.length; i++) {
								if(this.appointments[i].id == appointmentId) {
							//		this.appointments[i].symptoms = response.data.symptoms;
							//		this.appointments[i].drugs = response.data.drugs;
									this.appointment = this.appointments[i];
									this.appointment.effective = true;
								}
							}
						} else {
							this.patient = response.data.patient;
							this.appointments = response.data.appointments;
							for(let i=0; i < this.appointments.length; i++) {
								if(this.appointments[i].id == appointmentId) this.appointment = this.appointments[i];
							}
							this.documents = response.data.documents;
						}
						let refs = this.$refs;
						setTimeout(function(){ refs.clinicHistory.scrollToCurrentAppointment(); }, 300);
						this.tabOpen = 'anamnesis';//REVER
					}
				})
				.catch(error => console.log(error));
		},
		getTabName() {
			if (this.tabOpen === "clinical") return this.$gettext("Clinic H.");
			else if (this.tabOpen === "anamnesis") return this.$gettext("Anamnesis");
			else if (this.tabOpen === "tests") return this.$gettext("Tests");
			else if (this.tabOpen === "diagnosis") return this.$gettext("Diagnosis");
			else if (this.tabOpen === "treatments")
				return this.$gettext("Treatments");
			return "";
		},
		importAppointment(importAppId, topic) {
			const patientId = this.$route.params.pid;
			const appointmentId = this.$route.params.aid;
			const formData = new FormData();
			//if (onlyDrugs) formData.append("category", "drugs");
			//else formData.append("category", "all");
			formData.append("category", topic);
			axios
				.post(
					"/patient/" +
						patientId +
						"/appointment/" +
						appointmentId +
						"/import/" +
						importAppId,
					formData
				)
				.then(response => {
					console.log(response.data);
					if (response.status === 200) {
						if (topic != 'drugs') {
							for(var i=0; i < this.appointments.length; i++) {
								if(this.appointments[i].id == appointmentId) {
									this.appointments[i] = response.data.appointment;
									this.appointment = this.appointments[i];
									break;
								}
							}
						} else {
							for(var h=0; h < this.appointments.length; h++) {
								if(this.appointments[h].id == appointmentId) {
									this.appointments[h].drugs = response.data.drugs;
									this.appointment = this.appointments[h];
									break;
								}
							}
						}
					}
				})
				.catch(error => console.log(error));
		},
		importAppointmentAnamnese(importAppId, topic) {
			this.importAppointment(importAppId, topic);
		},
		importAppointmentDrugs(importAppId) {
			this.importAppointment(importAppId, 'drugs');
		},
		getPricing() {
			this.loadingAppointmentPricing = true;
			axios
				.get("/appointment/" + this.$route.params.aid + "/pricing")
				.then(response => {
					console.log(response.data);
					this.loadingAppointmentPricing = false;
					this.showPricing = true;
					this.appointmentPriceNotes = response.data.appointment.price_notes;
					this.$store.dispatch("SET_APPOINTMENT", response.data.appointment);
					this.$store.dispatch("SET_PRICES_INFO", response.data.price_info);
				})
				.catch(error => {
					console.log(error);
					this.loadingAppointmentPricing = false;
				});
		},
		getPercentage() {
			let price = this.$store.getters.appointmentPriceResult((this.appointment.id));
      if(!price || price == 0 || !this.appointmentPrice) return null;
      if(price != this.appointmentPrice) return Math.round(((((this.appointmentPrice * 100) / price) - 100) + Number.EPSILON) * 100) / 100
      else return null;
		},
		saveAppointmentPrice() {
			let price = 0;
			if(this.$refs.appPrice.validate()) {
				this.savingAppointmentPrice = true;

				if(this.appointmentPrice) price = this.appointmentPrice;
				else price = this.$store.getters.appointmentPrice(this.appointment.id);

				let form = new FormData();
				form.append("price", price);
				form.append("price_notes", this.appointmentPriceNotes ? this.appointmentPriceNotes : '');
				
				axios
					.post("/appointment/" + this.$route.params.aid + "/pricing", form)
					.then(response => {
						console.log(response.data);
						this.$store.dispatch("SET_APPOINTMENT_PRICE", response.data.price);
						this.resetEditPrice();
						this.savingAppointmentPrice = false;
					})
					.catch(error => {
						console.log(error);
						this.savingAppointmentPrice = false;
					});
			}
		},
		resetEditPrice() {
			this.appointmentPrice = null;
			this.showEditPrice = false;
			this.showPricing = false;
		},
		getName(t) {
			if(t.code) {
				let code = t.code;
				if(code == 'general') return 'Sinais Vitais e Dados Antropométricos';
				if(code == 'ryodoraku') return 'Ryodoraku';
				if(code == 'tongue') return this.$gettext('Tongue');
				if(code == 'pulse') return this.$gettext('Pulse');
				if(code == 'abdominal-palpation') return this.$gettext('Abdominal Palpation');
				if(code == 'body-structure') return this.$gettext('Body Structure');
				if(code == 'osteo') return this.$gettext('Osteopathy');
				if(code == 'supp') return this.$gettext('Supplements');
				if(code == 'acu') return this.$gettext('Acupuncture');
				if(code == 'acu-auri') return this.$gettext('Auricular Acupuncture');
				if(code == 'cmm') return this.$gettext('Chinese Materia Medica');
				if(code == 'qg') return 'Qi Gong';
				if(code == 'tn') return 'Tui Na';
				if(code == 'nut') return this.$gettext('Nutrition');
			} else {
				return t.name;
			}
    }
	},
	computed: {
		items() {
			const patientId = this.$route.params.pid;
			const appointmentId = this.$route.params.aid;
			//const name = "Patient" + patientId;
			var appointmentsTab = "";
			if (this.$store.getters.hasRole("terapeut")) {
				appointmentsTab = "clinic";
			} else {
				appointmentsTab = "appointments";
			}
			var items = [
				// {
				// 	text: this.$gettext("Patients"),
				// 	disabled: false,
				// 	link: true,
				// 	to: { name: "patients" }
				// },
				{
					name: true,
					text: this.patient.name,
					disabled: false,
					link: true,
					to: { name: "patient", params: { pid: patientId } }
				},
				{
					text: this.$gettext("Appointments"),
					disabled: false,
					link: true,
					to: {
						name: "patient",
						params: { pid: patientId, tab: appointmentsTab }
					}
				},
				{
					name: true,
					text: this.appointment.start_date,
					disabled: false,
					link: true,
					to: {
						name: "appointment",
						params: { pid: patientId, aid: appointmentId }
					}
				},
				// {
				// 	text: this.getTabName(),
				// 	disabled: true,
				// 	link: true
				// }
			];
			return items;
		},
		required() {
			return value => !!value || this.$gettext("Required.");
		},
		isNumber() {
			return value => (!isNaN(value) && !isNaN(parseFloat(value))) || this.$gettext("Invalid.");
    },
		...mapGetters({
      tourStep: 'tourStepEvent'
    })
	},
	created() {
		this.tabOpen = this.tab;
		this.init();
	},
	watch: {
		tabOpen(value) {
			window.scrollTo(0,0);
			if (value == 'treatments') {
				this.loadingTreatments = true;
				const patientId = this.$route.params.pid;
				const appointmentId = this.$route.params.aid;
				axios
					.get("/patient/" + patientId + "/appointment/" + appointmentId + "/treatments")
					.then(response => {
						console.log(response.data);
						if (response.status === 200) {
							this.appointment.supplements = response.data.supplements;
							this.appointment.formulas = response.data.formulas;
							this.appointment.acu_points = response.data.acu_points;
							this.appointment.other_treatments = response.data.other_treatments;
							this.appointment.custom_treatments = response.data.custom_treatments;
							this.$store.dispatch("SET_ACU_POINTS", {points: response.data.acu_points, ashi_points: this.appointment.ashi_points});
							this.loadingTreatments = false;
						}
					})
			}
			if(value == 'diagnosis') {
				this.loadingDiagnosis = true;
				const patientId = this.$route.params.pid;
				const appointmentId = this.$route.params.aid;
				axios
					.get("/patient/" + patientId + "/appointment/" + appointmentId + "/diagnosis")
					.then(response => {
						console.log(response.data);
						if (response.status === 200) {
							for(let i=0; i < this.appointments.length; i++) {
								if(this.appointments[i].id == appointmentId) {
									this.appointments[i].symptoms = response.data.symptoms;
									this.appointment = this.appointments[i];
								}
							}
							this.loadingDiagnosis = false;
						}
					})
			}
			if(value == 'tests') {
				this.loadingTests = true;
				const patientId = this.$route.params.pid;
				const appointmentId = this.$route.params.aid;
				axios
					.get("/patient/" + patientId + "/appointment/" + appointmentId + "/tests")
					.then(response => {
						console.log(response.data);
						if (response.status === 200) {
							this.appointment.tests = response.data.tests;
							this.appointment.custom_tests = response.data.custom_tests;
							this.loadingTests = false;
						}
					})
			}
			if(value == 'anamnesis') {
				this.loadingAnamnesis = true;
				const patientId = this.$route.params.pid;
				const appointmentId = this.$route.params.aid;
				axios
					.get("/patient/" + patientId + "/appointment/" + appointmentId + "/anamnesis")
					.then(response => {
						console.log(response.data);
						if (response.status === 200) {
							for(let i=0; i < this.appointments.length; i++) {
								if(this.appointments[i].id == appointmentId) {
									this.appointments[i].symptoms = response.data.symptoms;
									this.appointments[i].drugs = response.data.drugs;
									this.appointment = this.appointments[i];
								}
							}
							this.loadingAnamnesis = false;
						}
					})
			}
			if (value == 'clinical') {
				this.clinicKey += 1;
			}
		},
		tourStep(value) {
			if(value == 'tour-tests') {
				this.tabOpen = 'tests';
			}
			else if(value == 'tour-diagnosis') {
				this.tabOpen = 'diagnosis';
			}
			else if(value == 'tour-treatments') {
				this.tabOpen = 'treatments';
			}
		}
	}
}
</script>

<style scoped>
.v-slide-group {
	box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.08);
	margin-bottom: 10px;
}
</style>